import { camelCase, startCase } from 'lodash';
import { awAccountManagementApi } from './Base';
import {
  Comments,
  Enrollment,
  FilterEnrollmentsQuery,
  FilterEnrollmentsQueryResponse,
  ProcessedEnrollment,
} from './models';
import { RecordTypeEnum, SortOrder } from './types';

export const BASE_ENROLLMENTS_PATH = '/enrollment/v1/enrollments';
export const enrollmentsFilterPath = `${BASE_ENROLLMENTS_PATH}:filter`;
export const pageSize = 1000;

export const postProcessGetEnrollments: (data: Enrollment[]) => ProcessedEnrollment[] = (
  enrollmentsData: Enrollment[]
) =>
  enrollmentsData?.map((enrollment) => ({
    ...enrollment,
    clientCaseId: enrollment?.clientCaseId ? enrollment?.clientCaseId : enrollment?.clientId,
    actions: {
      rowStatus: enrollment?.status,
    },
    status: startCase(camelCase(enrollment?.status)),
  }));

// Filter for enrollments/templates on the basis of filter query.
export async function filterEnrollments({
  pageToken,
  query,
  pkg,
}: {
  pageToken?: string;
  query: FilterEnrollmentsQuery;
  pkg: string;
}): Promise<FilterEnrollmentsQueryResponse> {
  try {
    const res = await awAccountManagementApi.post<FilterEnrollmentsQueryResponse>(enrollmentsFilterPath, {
      pkg,
      pageSize,
      pageToken: pageToken ?? '*',
      query,
    });
    const data = res?.data;
    return {
      enrollments: postProcessGetEnrollments(data?.enrollments),
      nextPageToken: data?.nextPageToken,
      totalSize: data?.totalSize,
    };
  } catch (error) {
    console.error('Failed to filter enrollments', error);
    return { enrollments: [], nextPageToken: undefined, totalSize: 0 };
  }
}

// List enrollments
export async function listEnrollments(pkg: string, pageToken?: string): Promise<FilterEnrollmentsQueryResponse> {
  return filterEnrollments({
    pageToken,
    pkg,
    query: {
      recordType: RecordTypeEnum.ENROLLMENT,
      sorts: [
        {
          fieldName: 'createTime',
          sortOrder: SortOrder.SORT_ORDER_DESCENDING,
        },
      ],
    },
  });
}

// List templates
export async function listTemplates(pkg: string, pageToken?: string): Promise<FilterEnrollmentsQueryResponse> {
  return filterEnrollments({
    pageToken,
    pkg,
    query: {
      recordType: RecordTypeEnum.TEMPLATE,
      sorts: [
        {
          fieldName: 'createTime',
          sortOrder: SortOrder.SORT_ORDER_DESCENDING,
        },
      ],
    },
  });
}

// To Fetch an existing enrollment
export async function getEnrollment(enrollmentID: string): Promise<Enrollment> {
  try {
    const res = await awAccountManagementApi.get(`${BASE_ENROLLMENTS_PATH}/${enrollmentID}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Create an Enrollment
export async function createEnrollment(enrollmentData: Enrollment): Promise<Enrollment> {
  try {
    const res = await awAccountManagementApi.post(BASE_ENROLLMENTS_PATH, enrollmentData);
    return res?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Create a Template
export async function createTemplate(enrollmentData: Enrollment): Promise<Enrollment | undefined> {
  try {
    const res = await awAccountManagementApi.post(BASE_ENROLLMENTS_PATH, enrollmentData, {
      params: { recordType: 'RECORD_TYPE_TEMPLATE' },
    });
    return res?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Update an Enrollment
export async function updateEnrollment(enrollmentID: string, enrollmentData: Enrollment): Promise<Enrollment> {
  try {
    const updateTime = enrollmentData?.updateTime;
    const updateEnrollmentPath = `${BASE_ENROLLMENTS_PATH}/${enrollmentID}`;
    const res = await awAccountManagementApi.put(updateEnrollmentPath, enrollmentData, {
      params: { lastSeenUpdateTime: updateTime },
    });
    return res?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Update an Template
export async function updateTemplate(enrollmentID: string, enrollmentData: Enrollment): Promise<Enrollment> {
  try {
    const updateTime = enrollmentData?.updateTime;
    const updateEnrollmentPath = `${BASE_ENROLLMENTS_PATH}/${enrollmentID}`;
    const res = await awAccountManagementApi.put(updateEnrollmentPath, enrollmentData, {
      params: { lastSeenUpdateTime: updateTime, recordType: 'RECORD_TYPE_TEMPLATE' },
    });
    return res?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Update the status of an Enrollment
export async function updateEnrollmentStatus(enrollmentID: string, status: string, pkg: string): Promise<Enrollment> {
  try {
    const requestBody = { pkg, status };
    return await awAccountManagementApi.post(`${BASE_ENROLLMENTS_PATH}/${enrollmentID}:updateStatus`, requestBody);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Delete an Enrollment
export async function deleteEnrollment(enrollmentID: string, entityType: string): Promise<Enrollment> {
  try {
    return await awAccountManagementApi.delete(`${BASE_ENROLLMENTS_PATH}/${enrollmentID}`);
  } catch (error) {
    if (error?.response?.status === 404) {
      throw new Error(`This ${entityType} has already been deleted and cannot be deleted again.`);
    }
    console.error(error);
    throw error;
  }
}

// To List Comments of an Enrollment
export async function listComments(enrollmentID: string): Promise<Comments[]> {
  try {
    const res = await awAccountManagementApi.get(`${BASE_ENROLLMENTS_PATH}/${enrollmentID}/comments`);
    return res?.data?.comments;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// To Create Comment for an Enrollment
export async function createComment(
  enrollmentID: string,
  commentsData: string,
  userId: string | undefined
): Promise<Comments> {
  const commentsBody = {
    content: commentsData.trim(),
    author: userId,
  };
  try {
    const res = await awAccountManagementApi.post(`${BASE_ENROLLMENTS_PATH}/${enrollmentID}/comments`, commentsBody);
    return res?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}
